import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Button from '@repo/ui/button'
import { LocationThreeIcon, OptionIcon, TravelTwoIcon, XIcon } from '@repo/ui/icons'
import LineTab from '@repo/ui/tabs'
import Typography from '@repo/ui/typography'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { getAllCseReductionRules } from '../../../../api/cse/cse-reduction-rules'
import { getCseByCode } from '../../../../api/cse/get-cse-by-code'
import { getJourney } from '../../../../api/products/get-journey'
import { useFilters } from '../../../../lib/hooks/use-filters'
import { FamilyArrangementEnum } from '../../../../lib/types/enums/ol/family-arrangement.enum'
import { EnrollmentPropositionInput } from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition-input'
import { JourneyFilters } from '../../../../lib/types/models/product/journey'
import {
  transformJourneyFiltersToUrl,
  transformUrlToJourneyFilters
} from '../../../../lib/utils/filters-mapping/journey'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import Form, { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import { ENROLLMENT_PROPOSITION_SCHEMA } from '../utils/enrollments-utils'
import EnrollmentHospitalityConditions from './enrollment-hospitality-conditions'
import EnrollmentPropositionFamilyArrangementModal from './enrollment-proposition-family-arrangement-modal'
import EnrollmentPropositionTrips from './enrollment-proposition-trips'
import JourneyListSelection from './journey-list-selection'
import JourneyListSelectionFilter from './journey-list-selection-filter'

export type EnrollmentPropositionFormProps = {
  defaultValues?: EnrollmentPropositionInput
  defaultIndex?: number
  isLoading?: boolean
  onInputChange?: (input: EnrollmentPropositionInput) => void
  handleSubmit: (values: EnrollmentPropositionInput) => void
}

const initialValues: EnrollmentPropositionInput = {
  declaredFriends: [],
  journey: undefined,
  familyArrangement: undefined,
  insurances: [],
  options: [],
  journeyTripPlace: undefined,
  mainTrip: undefined,
  secondaryTrip: null,
  preferredPlace: null
}

const EnrollmentPropositionForm = ({
  defaultIndex,
  defaultValues,
  isLoading,
  onInputChange,
  handleSubmit
}: EnrollmentPropositionFormProps) => {
  const [cseCode, setCseCode] = useState<string | undefined>(defaultValues?.cseCode ?? undefined)
  const [activeIndex, setActiveIndex] = useState<number>(defaultIndex || 0)
  const [valuesToConfirm, setValuesToConfirm] = useState<EnrollmentPropositionInput>()
  const queryClient = useQueryClient()
  const form = useForm<EnrollmentPropositionInput>({
    resolver: zodResolver(ENROLLMENT_PROPOSITION_SCHEMA),
    defaultValues,
    mode: FORM_MODE
  })

  const journeyFilter = useFilters<JourneyFilters>({
    initialFilters: { published: true },
    options: { allowSearchParam: false },
    transformFilterToUrl: transformJourneyFiltersToUrl,
    transformUrlToFilter: transformUrlToJourneyFilters
  })

  const submitProposition = (values: EnrollmentPropositionInput) => {
    handleSubmit({
      ...values,
      pricing: {
        ...values.pricing,
        includeApplicationFees: defaultValues?.pricing?.includeApplicationFees ?? true
      }
    })
  }

  // a stay offering both FA1E and FA2E needs the family arrangement chosen before saving
  const onSubmit = async (values: EnrollmentPropositionInput) => {
    if (values.journey && !values.familyArrangement) {
      const { id } = values.journey
      const stay = await queryClient.fetchQuery({
        queryKey: ['journey', id],
        queryFn: () => getJourney(id)
      })
      if (stay.fa1eIncluded && stay.fa2eIncluded) {
        setValuesToConfirm(values)
        return
      }
    }
    submitProposition(values)
  }
  const journey = form.watch('journey')

  async function updateCseValue() {
    const normalizedCseCode = cseCode?.trim()
    if (!normalizedCseCode) return

    try {
      const cse = await getCseByCode(normalizedCseCode, 'BASIC')

      if (!cse.isActive) {
        setCseCode(undefined)
        form.setError('cseCode', { message: "Le CSE n'est pas actif" })

        return
      }

      const cseReductions = await getAllCseReductionRules(cse.id, { count: true })

      if (cseReductions.total === 0) {
        setCseCode(undefined)
        form.setError('cseCode', { message: "Le CSE n'a pas de règles de réduction" })

        return
      }

      // a new CSE limits the stays to its selection: the stay is picked again from the filtered list
      if (cse.code !== form.getValues('cseCode') && form.getValues('journey'))
        form.reset({
          ...initialValues,
          cseCode: cse.code,
          declaredFriends: form.getValues('declaredFriends')
        })
      else form.setValue('cseCode', cse.code)
      form.clearErrors('cseCode')
    } catch {
      setCseCode(undefined)
      form.setError('cseCode', { message: 'Le code CSE saisi est invalide' })
    }
  }

  const iconColorMap = (index: number) => {
    return activeIndex === index
      ? tailwindColors.primary.DEFAULT
      : tailwindColors.grey.light.active
  }

  const onIndexChange = (index: number) => {
    setActiveIndex(index)
  }

  const onNext = () => {
    setActiveIndex((prev) => prev + 1)
  }

  const onPrevious = () => {
    setActiveIndex((prev) => prev - 1)
  }

  useEffect(() => {
    setActiveIndex(defaultIndex || 0)
  }, [defaultIndex])

  useEffect(() => {
    const subscription = form.watch((values) => {
      if (onInputChange) onInputChange(values as EnrollmentPropositionInput)
    })

    // Cleanup subscription
    return () => subscription.unsubscribe()
  }, [form, onInputChange])

  return (
    <Form form={form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <LineTab
          tabs={[
            {
              label: 'Sélectionner un séjour',
              icon: <TravelTwoIcon color={iconColorMap(0)} />,
              component: (
                <>
                  <div className={cn({ hidden: activeIndex !== 0 })}>
                    <div className="flex flex-col items-start gap-2 mt-6">
                      <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
                        Code Partenaire CSE
                      </Typography>

                      <FormField
                        control={form.control}
                        name={'cseCode'}
                        render={({ field }) => (
                          <FormItem className="w-1/3">
                            <FormControl>
                              <ControlledInput
                                {...field}
                                type="text"
                                label="Code Partenaire CSE"
                                value={cseCode}
                                onChange={(value) =>
                                  setCseCode(typeof value === 'string' ? value : undefined)
                                }
                                suffix={
                                  <div className="flex items-center gap-2">
                                    {field.value && (
                                      <button
                                        type="button"
                                        className="bg-white-active rounded-sm w-5 h-5 flex items-center justify-center"
                                        onClick={() => {
                                          field.onChange(null)
                                          setCseCode(undefined)
                                          form.clearErrors('cseCode')
                                        }}
                                      >
                                        <XIcon
                                          width="15"
                                          height="15"
                                          color={tailwindColors.grey.lighter}
                                        />
                                      </button>
                                    )}
                                    <Button
                                      label="Appliquer"
                                      variant="link"
                                      type="button"
                                      disabled={!cseCode}
                                      onClick={updateCseValue}
                                    />
                                  </div>
                                }
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="h-4" />

                    <JourneyListSelectionFilter
                      filter={journeyFilter.filter}
                      onResetFilter={journeyFilter.resetFilters}
                      onSelectedFilterChange={journeyFilter.onSelectedFilterChange}
                    />
                    <div className="h-3" />
                  </div>

                  <JourneyListSelection
                    cseCode={form.watch('cseCode')}
                    filterProps={journeyFilter}
                    journeyId={journey?.id}
                    onSelectJourney={(journey) => {
                      if (journey.id !== form.watch('journey')?.id)
                        form.reset({
                          ...initialValues,
                          journey,
                          cseCode: form.watch('cseCode'),
                          declaredFriends: form.watch('declaredFriends')
                        })
                      else form.setValue('journey', journey)
                    }}
                    onSubmit={onNext}
                  />
                </>
              ),
              disabled: true
            },
            {
              label: "Options et modalités d'accueil",
              icon: <OptionIcon color={iconColorMap(1)} />,
              component: journey && (
                <EnrollmentHospitalityConditions
                  journeyId={journey.id}
                  onSubmit={onNext}
                  onCancel={onPrevious}
                />
              ),
              disabled: true
            },
            {
              label: 'Voyages',
              icon: <LocationThreeIcon color={iconColorMap(2)} />,
              component: journey && (
                <EnrollmentPropositionTrips
                  journeyId={journey.id}
                  onCancel={onPrevious}
                  isLoading={isLoading}
                  onSubmit={() => form.handleSubmit(onSubmit)()}
                />
              ),
              disabled: true
            }
          ]}
          activeTab={activeIndex}
          wrapperClassName="px-4"
          onTabChange={onIndexChange}
        />
      </form>
      <EnrollmentPropositionFamilyArrangementModal
        isOpen={!!valuesToConfirm}
        onClose={() => setValuesToConfirm(undefined)}
        onSubmit={(familyArrangement: FamilyArrangementEnum) => {
          if (!valuesToConfirm) return
          form.setValue('familyArrangement', familyArrangement)
          submitProposition({ ...valuesToConfirm, familyArrangement })
          setValuesToConfirm(undefined)
        }}
      />
    </Form>
  )
}

export default EnrollmentPropositionForm
